import type { VFC } from 'react';
import axios from 'axios';
import { Alert, Button, Form, Input, Space } from 'antd';
import { useMutation } from 'react-query';
import { useSetRecoilState } from 'recoil';
import { sessionState } from 'store';
import { useServices } from 'containers/services/useServices';
import { isStrapiApiErrorResponse } from 'utils/isStrapiApiErrorResponse';
import type { Credentials } from 'lib/services/authApi';
import { Icon } from '@/components/icon';
import styled from 'styled-components';

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 12px;
  }

  .anticon {
    color: ${({ theme }) => theme.color.theme.primary};
  }

  input.ant-input {
    text-align: center;
  }
`;

export const LoginForm: VFC = () => {
  const setSession = useSetRecoilState(sessionState);
  const { adminApiClient } = useServices();

  const {
    mutate: login,
    isLoading,
    error,
  } = useMutation(async (credentials: Credentials) => await adminApiClient.login(credentials), {
    onError: e => {
      console.error(e);
    },
    onSuccess: ({ jwt, user }) => {
      setSession({ jwt, userInfo: user, isAuthenticated: true });
    },
  });

  const errorMessage =
    error &&
    axios.isAxiosError(error) &&
    error.response?.status === 400 &&
    isStrapiApiErrorResponse(error.response?.data)
      ? error.response.data.error.message
      : 'An unknown error occurred';

  return (
    <Space direction="vertical" size={16} style={{ display: 'flex' }}>
      {error && <Alert showIcon title={errorMessage} type="error" />}
      <StyledForm layout="vertical" onFinish={login}>
        <Form.Item
          required
          name="identifier"
          rules={[
            { required: true, validateTrigger: 'onSubmit' },
            { type: 'email', validateTrigger: 'onSubmit' },
          ]}
        >
          <Input placeholder="Email" prefix={<Icon iconString="user" />} />
        </Form.Item>
        <Form.Item required name="password" rules={[{ required: true, validateTrigger: 'onSubmit' }]}>
          <Input placeholder="Password" prefix={<Icon iconString="lock" />} type="password" />
        </Form.Item>
        <Button className="w-100" htmlType="submit" loading={isLoading} type="primary">
          Log in
        </Button>
      </StyledForm>
    </Space>
  );
};
